import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { FormatOptionLabelMeta } from 'react-select';
import { SingleValue } from 'react-select';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { useDeepCompareMemo } from '@clatter/platform';
import { DropdownSelect } from '../../../components';
import { authSelectors, setSelectedThemeId, addNotification } from '../../../store';
import { useGetAssetThumbnailUrlQuery, useGetPresentationThemesQuery } from '../../../services';
import { sortByDate } from '../../../helpers/sorting';

export const NONE_THEME_VALUE = 'none';

interface ThemeOptionItem extends IDropdownSelectOptionItem {
  assetId?: number;
  thumbnailUrl?: string;
  isDefault?: boolean;
  isGlobal?: boolean;
}

interface Props {
  selectedThemeId?: number | null;
  defaultThemeId?: number | null;
  isLoadingDefault?: boolean;
  viewStateKey?: string;
  slideRepositoryId?: number;
  compact?: boolean;
  onChange?: (theme: SingleValue<ThemeOptionItem>) => void;
}

const StyledOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  user-select: none;
  pointer-events: none;
`;

const StyledThumbnail = styled.div<{ $url?: string }>`
  width: 72px;
  height: 45px;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background-color: ${({ theme }) => theme.palette.cardBorder};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const StyledLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #111827;
`;

const StyledDefaultText = styled.span`
  font-size: 10px;
  color: #4B5563;
  margin-top: 1px;
`;

const defaultSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
  }),
  option: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
  }),
};

const compactSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    cursor: 'pointer',
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    fontWeight: 600,
    borderColor: state.isFocused || state.menuIsOpen ? '#0A6EFF' : '#D1D5DB',
    borderWidth: '1px',
    borderRadius: '5px',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#0A6EFF' },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '0 6px',
    height: '26px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '26px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: '0 4px',
    color: 'inherit',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menu: (provided: any) => ({
    ...provided,
    width: '260px',
    right: 0,
    left: 'auto',
    borderRadius: '10px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
    border: '1.5px solid #E5E7EB',
    padding: '8px',
    marginTop: '4px',
    overflow: 'hidden',
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '7px 8px',
    borderRadius: '7px',
    cursor: 'pointer',
    backgroundColor: state.isSelected ? '#EFF6FF' : state.isFocused ? '#F9FAFB' : 'white',
    border: `2px solid ${state.isSelected ? '#0A6EFF' : 'transparent'}`,
    color: 'inherit',
  }),
};

const ThemeOptionLabel = memo<{ option: ThemeOptionItem }>(({ option }) => {
  const { data: thumbnailUrl } = useGetAssetThumbnailUrlQuery(
    option.assetId != null ? { assetId: option.assetId } : skipToken,
  );
  const url = (option.assetId && thumbnailUrl) || option.thumbnailUrl;

  return (
    <StyledOptionContainer>
      <StyledThumbnail $url={url} />
      <StyledLabelContainer>
        <StyledLabel>{option.label}</StyledLabel>
        {option.isDefault && <StyledDefaultText>Default</StyledDefaultText>}
      </StyledLabelContainer>
    </StyledOptionContainer>
  );
});

const formatOptionLabel = (compact: boolean) => (option: ThemeOptionItem, { context }: FormatOptionLabelMeta<ThemeOptionItem>) => {
  if (compact && context === 'value') {
    return <StyledLabel style={{ fontSize: '11px' }}>Theme: {option.label}</StyledLabel>;
  }
  return <ThemeOptionLabel option={option} />;
};

export const SlidesThemeSelector: FC<Props> = ({
  selectedThemeId: selectedThemeIdProp,
  defaultThemeId,
  isLoadingDefault,
  viewStateKey,
  slideRepositoryId,
  compact = false,
  onChange,
}) => {
  const dispatch = useDispatch();

  const storeThemeId = useSelector((state: any) =>
    viewStateKey ? state.viewState?.byKey?.[viewStateKey]?.selectedThemeId ?? null : null,
  );
  const hasStoreEntry = useSelector((state: any) => (viewStateKey ? !!state.viewState?.byKey?.[viewStateKey] : false));
  const selectedThemeId = selectedThemeIdProp !== undefined ? selectedThemeIdProp : storeThemeId;

  const defaultOnChange = useCallback(
    (theme: SingleValue<ThemeOptionItem>) => {
      if (!viewStateKey) return;
      const themeId = theme?.value && theme.value !== NONE_THEME_VALUE ? Number(theme.value) : null;
      dispatch(setSelectedThemeId({ viewStateKey, themeId }));
    },
    [dispatch, viewStateKey],
  );

  const handleOnChange = onChange || defaultOnChange;

  // stable ref so the init effect doesn't re-run when the callback identity changes
  const handleOnChangeRef = useRef(handleOnChange);
  useEffect(() => {
    handleOnChangeRef.current = handleOnChange;
  });

  const isAdmin = useSelector(authSelectors.isAdmin);

  const { data: presentationThemes, isLoading } = useGetPresentationThemesQuery(undefined, {
    skip: !!isLoadingDefault,
  });

  const themesOptions: ThemeOptionItem[] = useDeepCompareMemo(() => {
    if (!presentationThemes || !Array.isArray(presentationThemes)) {
      return [];
    }

    const filteredThemes = slideRepositoryId
      ? presentationThemes.filter(
          (theme) => theme.SlideRepositoryID == null || theme.SlideRepositoryID === slideRepositoryId,
        )
      : presentationThemes;

    const sortedThemes = sortByDate([...filteredThemes], 'CreatedAt', 'asc');

    const mappedThemes = sortedThemes.map((theme: PresentationTheme) => ({
      value: theme.AssetVersionID?.toString(),
      label:
        theme.Name ||
        theme.AssetVersion?.Asset?.DisplayName ||
        theme.AssetVersion?.FileName ||
        `Theme ${theme.AssetVersionID}`,
      assetId: (theme as any).AssetVersion?.AssetID ? Number((theme as any).AssetVersion.AssetID) : undefined,
      isDefault: theme.IsDefault,
      isGlobal: slideRepositoryId ? theme.SlideRepositoryID == null : false,
    }));

    if (isAdmin) return [{ value: NONE_THEME_VALUE, label: 'None' }, ...mappedThemes];

    return mappedThemes;
  }, [presentationThemes, isAdmin]);

  // track if initial default selection has been made
  const [hasInitialized, setHasInitialized] = useState(false);

  // find the selected option from themesOptions based on selectedThemeId
  const selectedOption = useDeepCompareMemo(() => {
    if (isAdmin && selectedThemeId === null) {
      return hasInitialized || hasStoreEntry
        ? themesOptions.find((option) => option.value === NONE_THEME_VALUE) || null
        : null;
    }
    return themesOptions.find((option) => option.value === `${selectedThemeId}`) || null;
  }, [selectedThemeId, themesOptions, hasInitialized]);

  const handleChange = (newValue: SingleValue<ThemeOptionItem>, _actionMeta: any) => {
    setHasInitialized(true);
    handleOnChange(newValue);
  };

  useEffect(() => {
    if (
      !hasInitialized &&
      !selectedThemeId &&
      !hasStoreEntry &&
      !isLoadingDefault &&
      !isLoading &&
      themesOptions.length > 0
    ) {
      const filtered = themesOptions.filter((t) => t.value !== NONE_THEME_VALUE);

      const matched = defaultThemeId ? filtered.find((t) => t.value === `${defaultThemeId}`) : null;

      if (defaultThemeId && !matched) {
        dispatch(addNotification({ type: 'warning', message: 'Saved theme not found. Setting default theme.' }));
      }

      const option = matched ?? filtered.find((t) => t.isDefault) ?? filtered[0];

      if (option) {
        setHasInitialized(true);
        handleOnChangeRef.current(option);
      }
    }
  }, [
    selectedThemeId,
    defaultThemeId,
    isLoadingDefault,
    isLoading,
    themesOptions,
    hasInitialized,
    hasStoreEntry,
    dispatch,
  ]);

  return (
    <DropdownSelect
      value={selectedOption}
      options={themesOptions}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel(compact)}
      placeholder="Select a theme"
      isLoading={isLoading}
      isSearchable={false}
      styles={compact ? compactSelectStyles : defaultSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  );
};
